import React, { PureComponent, useMemo } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import FormikProvider from '../../shared/forms/formikProvider.component';
import Modal from '../../shared/modals/modal.component';
import Form from '../../shared/forms/form.component';

import schema from './adjustmentForm.schema';
import { isNotPreTaxAdjustmentTypeSelected } from './adjustmentForm.helpers';

import {
  saveAdjustment,
  fetchInitialDataForAdjustmentModal,
  fetchAdjustmentAttachments,
} from '../adjustmentsTab/store/actions';
import { OptionsPropType } from '../../shared/propTypes/common';

import { encodeCountryTaxType } from '../../utils/encodeDecodeCTT';

import { AccountPropTypes } from '../../shared/propTypes/accounts';
import { adjustmentTypesSelector, countryTaxTypesOptionsSelector, statCalcSelector } from '../store/selectors';
import { YES_FLAG, NO_FLAG } from '../../shared/constants';

import {
  accountsSelector,
  savingAdjustmentSelector,
  fetchingInitialDataForAdjustmentModalSelector,
  fetchingAttachmentsSelector,
  attachmentsSelector,
} from '../adjustmentsTab/store/selectors';
import {
  bookTypesOptionsSelector,
  periodOrCurrentSelector,
  countryTaxTypeDescriptionsSelector,
  bookToTaxCodeEnabledFlagSelector,
  companyCodeSelector,
} from '../../shared/store/selectors';
import { currentClientSelector } from '../../userData/store/selectors';
import { PeriodPropTypes } from '../../shared/propTypes/period';
import { AdjustmentPropTypes } from './addEditAdjustmentModal.propTypes';
import { adjustmentTypeOptionsSelector, accountOptionsSelector } from './addEditAdjustmentModal.selectors';
import { requiredWhenPresent } from '../../shared/propTypes';

const ModalTitles = {
  ADD: 'Add Adjustment',
  EDIT: 'Edit Adjustment',
};

class AddEditAdjustmentModal extends PureComponent {
  static propTypes = {
    hideModal: PropTypes.func.isRequired,
    isVisible: PropTypes.bool,
    isFetchingInitialData: PropTypes.bool.isRequired,
    accounts: PropTypes.arrayOf(AccountPropTypes).isRequired,
    bookTypes: OptionsPropType,
    entityToEdit: AdjustmentPropTypes,
    adjustmentTypes: OptionsPropType,
    countryTaxTypes: OptionsPropType,
    saveAdjustment: PropTypes.func.isRequired,
    isSavingInProgress: PropTypes.bool.isRequired,
    fetchInitialDataForAdjustmentModal: PropTypes.func.isRequired,
    fetchAdjustmentAttachments: PropTypes.func.isRequired,
    period: PeriodPropTypes.isRequired,
    currentClient: PropTypes.shape({ fiscalStartMonth: PropTypes.string }),
    companyCode: PropTypes.string.isRequired,
    countryTaxTypeDescriptions: PropTypes.objectOf(PropTypes.string.isRequired),
    statCalc: requiredWhenPresent(['isVisible'], PropTypes.string),
    bookToTaxCodeEnabledFlag: PropTypes.bool.isRequired,
    openForEdit: PropTypes.bool,
    fetchingAttachments: PropTypes.bool.isRequired,
    attachments: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      fileName: PropTypes.string,
      fileType: PropTypes.string,
      fileImage: PropTypes.string,
    })),
    isMultiTaxNonUS: PropTypes.bool,
  };

  state = {
    addAnother: false,
  };

  componentDidMount() {
    this.props.fetchInitialDataForAdjustmentModal();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.openForEdit !== this.props.openForEdit && this.props.openForEdit) {
      this.props.fetchAdjustmentAttachments(this.props.entityToEdit.adjustmentId);
    }
  }

  save = (formData, { setSubmitting, resetForm }) => {
    const {
      period,
      saveAdjustment,
      openForEdit,
      entityToEdit,
      companyCode,
      adjustmentTypes,
    } = this.props;
    const { addAnother } = this.state;

    this.setState({ addAnother: false }, () => {
      saveAdjustment({
        adjustmentData: {
          ...formData,
          periodName: period.name,
          adjustmentId: entityToEdit ? String(entityToEdit.adjustmentId) : null,
        },
        params: {
          period,
          companyCode,
          adjustmentTypes,
        },
        openForEdit,
        leaveModalOpen: addAnother,
      }, resetForm);
      setSubmitting(false);
    });
  };

  renderModal = ({ formik, schema }) => {
    const {
      openForEdit,
      countryTaxTypes,
      period,
      currentClient,

      adjustmentTypes,
      countryTaxTypeDescriptions,
      statCalc,
      accounts,
      entityToEdit,

      bookTypes,
      bookToTaxCodeEnabledFlag,
      hideModal,
      isVisible,
      isSavingInProgress,
      isFetchingInitialData,
      fetchingAttachments,
      isMultiTaxNonUS,
    } = this.props;

    const submit = () => {
      this.setState({
        addAnother: false,
      }, () => formik.handleSubmit());
    };

    const submitAndAddAnotherAction = () => {
      this.setState({
        addAnother: true,
      }, () => formik.handleSubmit());
    };

    const reset = () => {
      hideModal();
      formik.resetForm();
    };

    const adjustmentTypeOptions = adjustmentTypeOptionsSelector({
      adjustmentTypes,
      countryTaxType: formik.values.countryTaxType,
      countryTaxTypeDescriptions,
      statCalc,
    });

    const amountFromOptions = useMemo(() => isNotPreTaxAdjustmentTypeSelected({ formik })
      ? [
        { label: 'Manually entered', value: NO_FLAG },
        { label: 'Account in Trial Balance', value: YES_FLAG },
      ]
      : [
        { label: 'Manually entered', value: NO_FLAG },
      ], [formik.values.adjustmentType]); // eslint-disable-line react-hooks/exhaustive-deps

    const accountOptions = accountOptionsSelector({
      account: formik.values.account,
      accounts,
      amountFrom: formik.values.amountFrom,
      adjustmentToEdit: entityToEdit,
    });

    return (
      <Modal
        title={openForEdit ? ModalTitles.EDIT : ModalTitles.ADD}
        closeAction={reset} // eslint-disable-line react/jsx-no-bind
        dismissText="Cancel"
        dismissAction={reset} // eslint-disable-line react/jsx-no-bind
        submitText="Save"
        submitAction={submit} // eslint-disable-line react/jsx-no-bind
        submitAndAddAnotherAction={!openForEdit ? submitAndAddAnotherAction : null}
        submitAndAddAnotherText="Save and Add Another"
        isLoading={isFetchingInitialData || fetchingAttachments}
        isSubmitting={formik.isSubmitting || isSavingInProgress}
        isVisible={isVisible}
      >
        <Form
          formik={formik}
          schema={schema}
          openForEdit={openForEdit}
          formContext={{
            period,
            countryTaxTypes,
            adjustmentTypeOptions,
            amountFromOptions,
            accountOptions,
            bookTypes,
            bookToTaxCodeEnabledFlag,
            isMultiTaxNonUS,
            firstMonth: currentClient.fiscalStartMonth || 'Jan',
          }}
        />
      </Modal>
    );
  };

  render() {
    const { entityToEdit, bookToTaxCodeEnabledFlag, countryTaxTypes, attachments } = this.props;

    const formattedEntityToEdit = entityToEdit
      ? {
        ...entityToEdit,
        countryTaxType: encodeCountryTaxType(entityToEdit.countryCode, entityToEdit.countryTaxType),
        adjustmentType: entityToEdit.type,
        bookToTaxDescription: entityToEdit.tagCode,
        files: attachments.map(file => ({
          name: file.fileName,
          image: file.fileImage,
          type: file.fileType,
          id: file.id,
        })),
      }
      : null;

    return (
      <FormikProvider
        schema={schema}
        entityToEdit={formattedEntityToEdit}
        onSubmit={this.save}
        render={this.renderModal}
        validationContext={{
          bookToTaxCodeEnabledFlag,
          countryTaxTypes,
        }}
      />
    );
  }
}

const mapStateToProps = state => ({
  period: periodOrCurrentSelector(state),
  currentClient: currentClientSelector(state),
  companyCode: companyCodeSelector(state),
  accounts: accountsSelector(state),
  bookTypes: bookTypesOptionsSelector(state),

  countryTaxTypes: countryTaxTypesOptionsSelector(state),
  adjustmentTypes: adjustmentTypesSelector(state),
  statCalc: statCalcSelector(state),
  countryTaxTypeDescriptions: countryTaxTypeDescriptionsSelector(state),
  bookToTaxCodeEnabledFlag: bookToTaxCodeEnabledFlagSelector(state),
  isFetchingInitialData: fetchingInitialDataForAdjustmentModalSelector(state),
  isSavingInProgress: savingAdjustmentSelector(state),
  fetchingAttachments: fetchingAttachmentsSelector(state),
  attachments: attachmentsSelector(state),
});

export default connect(
  mapStateToProps,
  {
    saveAdjustment,
    fetchInitialDataForAdjustmentModal,
    fetchAdjustmentAttachments,
  }
)(AddEditAdjustmentModal);
